import {
  ScheduledBankPaymentGroupStateEnum,
  TScheduledBankPaymentGroupState
} from '@/features/payments/types/BatchPayment';
import { batchPaymentsPage } from '@/utils/translations';

export const STATUS_LABEL: Record<TScheduledBankPaymentGroupState, string> = {
  approved: batchPaymentsPage.status.approved,
  rejected: batchPaymentsPage.status.rejected,
  pending_approval: batchPaymentsPage.status.pending_approval,
  in_progress: batchPaymentsPage.status.in_progress,
  completed: batchPaymentsPage.status.completed,
  completed_with_failures: batchPaymentsPage.status.completed_with_failures,
  failed: batchPaymentsPage.status.failed,
  expired: batchPaymentsPage.status.expired
};

export const STATUS_STYLE: Record<TScheduledBankPaymentGroupState, string> = {
  approved: 'text-emerge-teal',
  rejected: 'text-error-darkred',
  pending_approval: 'text-emerge-purple',
  in_progress: 'text-emerge-teal',
  completed: 'text-emerge-teal',
  completed_with_failures: 'text-emerge-orange',
  failed: 'text-error-darkred',
  expired: 'text-error-darkred'
};

/**
 * FAILED/COMPLETED_WITH_FAILURES both just mean "finished with at least one failure" - which of
 * the two the backend actually returns can't be trusted to mean all-failed vs some-failed (seen
 * in practice: a group can carry two failures and still not be COMPLETED_WITH_FAILURES vs FAILED
 * consistently). Single source of truth for that check, shared by BatchModal and
 * BatchOverviewModal rather than each re-deriving it under a different local name.
 */
export const isBatchIncomplete = (state: TScheduledBankPaymentGroupState): boolean =>
  state === ScheduledBankPaymentGroupStateEnum.FAILED ||
  state === ScheduledBankPaymentGroupStateEnum.COMPLETED_WITH_FAILURES;

/**
 * Whether every payment in an incomplete (FAILED/COMPLETED_WITH_FAILURES) batch actually failed,
 * not just some - true for a genuine FAILED state, and also for a COMPLETED_WITH_FAILURES state
 * where completedPayments is 0 (the backend has been observed returning COMPLETED_WITH_FAILURES
 * even when every payment failed - see isBatchIncomplete). False for any other state, or for
 * COMPLETED_WITH_FAILURES with at least one completed payment (a genuine partial failure).
 */
export const isBatchAllFailed = (
  state: TScheduledBankPaymentGroupState,
  completedPayments?: number
): boolean =>
  state === ScheduledBankPaymentGroupStateEnum.FAILED ||
  (state === ScheduledBankPaymentGroupStateEnum.COMPLETED_WITH_FAILURES && completedPayments === 0);

/**
 * An APPROVED payment that never went through multi-sign (no multiSignActionId - a plain
 * single-signer batch/scheduled payment) reads as "Scheduled" rather than "Approved" - it was
 * never actually approved by anyone, just scheduled to go out. Deliberately kept as a derived
 * check rather than its own ScheduledBankPaymentGroupStateEnum member: the backend never returns
 * a "scheduled" state - the row's real state is always APPROVED either way - so this is a purely
 * frontend display distinction, not a fourth real state every full Record keyed by that enum
 * (STATUS_LABEL/STATUS_STYLE above, ACTION_BY_STATE) would then have to account for.
 */
export const isScheduledWithoutMultiSign = (
  state: TScheduledBankPaymentGroupState,
  multiSignActionId?: string | null
): boolean => state === ScheduledBankPaymentGroupStateEnum.APPROVED && !multiSignActionId;

/**
 * Whether a batch's summary card (BatchModal) has anything worth showing before its full payee
 * list (BatchOverviewModal) - an approve/reject, retry, or mark-as-complete action, or a failure
 * banner explaining the state ("Skip Batch payment overview screen if not actionable"):
 * PENDING_APPROVAL, FAILED and COMPLETED_WITH_FAILURES are the only states that do; every other
 * state is a dead end on the summary screen (just a single "View payments" button), so BatchModal
 * skips straight to the overview instead. IN_PROGRESS is included in the skip - BatchOverviewModal
 * already tracks a live IN_PROGRESS -> COMPLETED/FAILED transition itself (see its
 * processingOutcome state), which only makes sense if the overview is already open when that
 * happens.
 *
 * Typed as a full Record for the same reason as ACTION_BY_STATE in paymentActions.ts - a new
 * backend state must declare which side of this line it falls on before it compiles.
 */
export const IS_BATCH_SUMMARY_ACTIONABLE: Record<TScheduledBankPaymentGroupState, boolean> = {
  [ScheduledBankPaymentGroupStateEnum.PENDING_APPROVAL]: true,
  [ScheduledBankPaymentGroupStateEnum.FAILED]: true,
  [ScheduledBankPaymentGroupStateEnum.COMPLETED_WITH_FAILURES]: true,
  [ScheduledBankPaymentGroupStateEnum.APPROVED]: false,
  [ScheduledBankPaymentGroupStateEnum.REJECTED]: false,
  [ScheduledBankPaymentGroupStateEnum.COMPLETED]: false,
  [ScheduledBankPaymentGroupStateEnum.EXPIRED]: false,
  [ScheduledBankPaymentGroupStateEnum.IN_PROGRESS]: false
};

/**
 * `forceCompleted` overrides the state-based check above to `false` regardless of state - once a
 * FAILED/COMPLETED_WITH_FAILURES batch has been force-completed (via Mark as complete in Batch
 * Overview), Retry/Mark as complete are both retired for good (see `showRetryPair` in BatchModal
 * and `showIncompleteActions` in BatchOverviewModal, which apply this same override), so the
 * summary card has nothing left to offer either - just the plain "View payments" button, same as
 * any other no-action state.
 */
export const isActionableBatchSummaryState = (
  state: TScheduledBankPaymentGroupState,
  forceCompleted?: boolean
): boolean => !forceCompleted && IS_BATCH_SUMMARY_ACTIONABLE[state];

/**
 * Delete was previously shown to every admin who could reach the DELETE action variant,
 * regardless of who actually created the payment/batch - only its own creator should see it.
 * Shared by BatchOverviewModal (batch delete) and ScheduledPaymentModal (individual payment
 * delete) so the rule can't drift between the two the way isAwaitingAdditionalApproval once did
 * (see useApprovalDisplayState).
 *
 * Fails closed on either side being unknown (undefined createdByCustomerId, or no logged-in
 * customer) rather than defaulting to "visible" - a payment with no recorded creator should hide
 * Delete for everyone, not show it to everyone.
 */
export const isPaymentCreator = (
  createdByCustomerId: string | undefined,
  currentCustomerId: string | undefined
): boolean => !!createdByCustomerId && createdByCustomerId === currentCustomerId;
